import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  PageHeader,
  FilterBar,
  EmptyState,
  StatusChip,
} from "../components/ui";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { FileText, Mail, Linkedin, Eye, Phone, ExternalLink } from "lucide-react";
import { adminAPI } from "../services/api";
import { useToast } from "../context/ToastContext";
import useListQueryParams from "../hooks/useListQueryParams";
import AdminDateRangeFilter from "../components/AdminDateRangeFilter.jsx";
import { statusChipColors } from "../theme";
import "./ModuleAdmin.css";

const STATUS_OPTIONS = [
  { value: "applied", label: "Applied" },
  { value: "interview", label: "Interview" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Better luck next time" },
];

function formatAppliedDate(createdAt) {
  if (!createdAt) return "—";
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const Applicants = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { query, setQuery } = useListQueryParams({
    page: "1",
    limit: "20",
    jobId: "",
    status: "",
    search: "",
    fromDate: "",
    toDate: "",
    sortOrder: "desc",
  });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const jobFilter = query.jobId || "";
  const statusFilter = query.status || "";
  const search = query.search || "";
  const fromDate = query.fromDate || "";
  const toDate = query.toDate || "";
  const sortOrder = query.sortOrder || "desc";
  const [viewingResumeId, setViewingResumeId] = useState("");
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const { data: careersData } = useQuery("careers-admin", adminAPI.getCareers);
  const { data: applicantsData, isLoading } = useQuery(
    [
      "applicants",
      jobFilter,
      statusFilter,
      search,
      fromDate,
      toDate,
      sortOrder,
      page,
      limit,
    ],
    () =>
      adminAPI.getApplicants({
        page,
        limit,
        jobId: jobFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        sortOrder,
      }),
  );

  const applicants = applicantsData?.data?.data || [];
  const pagination = applicantsData?.data?.pagination || {
    page: 1,
    totalPages: 1,
    total: applicants.length,
  };
  const categories = careersData?.data?.data?.positions?.categories || [];
  const allJobs = categories.flatMap((c) =>
    (c.jobs || []).map((j) => ({ ...j, categoryName: c.name })),
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => adminAPI.updateApplicantStatus(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("applicants");
        showToast({
          message: "Applicant status updated.",
          severity: "success",
        });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || "Failed to update status",
          severity: "error",
        }),
    },
  );

  const handleViewResume = async (row) => {
    if (!row?.id || !row.resumeUrl) return;
    setViewingResumeId(String(row.id));
    try {
      const res = await adminAPI.downloadApplicantResume(row.id);
      const mime = res.headers["content-type"] || "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const href = URL.createObjectURL(blob);
      window.open(href, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(href), 60_000);
    } catch (err) {
      let message = "Failed to open resume.";
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          /* keep default */
        }
      } else if (data?.message) {
        message = data.message;
      }
      showToast({ message, severity: "error" });
    } finally {
      setViewingResumeId("");
    }
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Job Applicants"
        subtitle="View and manage applications submitted from the careers page."
      />

      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filter by job</InputLabel>
          <Select
            value={jobFilter}
            label="Filter by job"
            onChange={(e) => setQuery({ jobId: e.target.value })}
          >
            <MenuItem value="">All applications</MenuItem>
            {allJobs.map((job) => (
              <MenuItem key={job.id} value={job.id}>
                {job.title} ({job.categoryName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setQuery({ status: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search name/email/phone"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
          sx={{ minWidth: 240 }}
        />
        <AdminDateRangeFilter
          startDate={fromDate}
          endDate={toDate}
          onStartDateChange={(v) => setQuery({ fromDate: v })}
          onEndDateChange={(v) => setQuery({ toDate: v })}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortOrder}
            label="Sort"
            onChange={(e) => setQuery({ sortOrder: e.target.value })}
          >
            <MenuItem value="desc">Latest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading applicants...</p>
        ) : applicants.length === 0 ? (
          <EmptyState
            title="No applicants yet"
            description="Applications from the careers page will appear here."
          />
        ) : (
          <table className="module-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Contact</th>
                <th>Job</th>
                <th>Exp & CTC (LPA)</th>
                <th>Source & Ref</th>
                <th>Profile</th>
                <th>Resume</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {applicants.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.fullName}</div>
                    {row.gender && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#64748b",
                          background: "#f1f5f9",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          display: "inline-block",
                          marginTop: "2px",
                        }}
                      >
                        {row.gender}
                      </span>
                    )}
                  </td>
                  <td>
                    <div>
                      <Link
                        href={`mailto:${row.email}`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.85rem",
                        }}
                      >
                        <Mail size={13} /> {row.email}
                      </Link>
                    </div>
                    {row.phone && (
                      <div style={{ marginTop: 2 }}>
                        <Link
                          href={`tel:${row.phone}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: "0.82rem",
                            color: "#475569",
                          }}
                        >
                          <Phone size={13} /> {row.phone}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td>
                    {row.job?.title ? (
                      <span title={row.job.location || ""}>
                        {row.job.title}
                        {row.job.deletedAt && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: "0.72rem",
                              color: "#dc2626",
                              backgroundColor: "#fee2e2",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: 500,
                            }}
                          >
                            Deleted
                          </span>
                        )}
                      </span>
                    ) : row.jobTitle ? (
                      <span>
                        {row.jobTitle}
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: "0.72rem",
                            color: "#dc2626",
                            backgroundColor: "#fee2e2",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Deleted
                        </span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {row.experience ? `${row.experience} yrs` : "0 yrs"}
                    </div>
                    {(row.currentCtc || row.expectedCtc) && (
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                        <span>Curr: {row.currentCtc ? `₹${row.currentCtc}L` : "—"}</span>
                        {" · "}
                        <span>Exp: {row.expectedCtc ? `₹${row.expectedCtc}L` : "—"}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontSize: "0.85rem" }}>{row.hearAbout || "—"}</div>
                    {row.referredBy && (
                      <div
                        style={{
                          fontSize: "0.74rem",
                          color: "#0369a1",
                          marginTop: 2,
                        }}
                        title={`Referred by: ${row.referredBy}`}
                      >
                        Ref: {row.referredBy}
                      </div>
                    )}
                  </td>
                  <td>
                    {row.linkedin ? (
                      <Link
                        href={row.linkedin.startsWith("http") ? row.linkedin : `https://${row.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          color: "#0a66c2",
                          "&:hover": {
                            color: "#004182",
                          },
                        }}
                        title="View profile"
                      >
                        <Linkedin size={18} />
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {row.resumeUrl ? (
                      <Link
                        component="button"
                        type="button"
                        onClick={() => handleViewResume(row)}
                        disabled={viewingResumeId === String(row.id)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <FileText size={14} />{" "}
                        {viewingResumeId === String(row.id)
                          ? "Opening…"
                          : "View"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Select
                      size="small"
                      value={row.status || "applied"}
                      onChange={(e) =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: e.target.value,
                        })
                      }
                      disabled={updateStatusMutation.isLoading}
                      sx={{
                        minWidth: 130,
                        backgroundColor:
                          statusChipColors[row.status]?.bg || "#f1f5f9",
                        color: statusChipColors[row.status]?.color || "#475569",
                        borderRadius: "16px",
                        fontWeight: 600,
                        fontSize: "0.80rem",
                        height: "28px",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            statusChipColors[row.status]?.border || "#cbd5e1",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            statusChipColors[row.status]?.border || "#cbd5e1",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            statusChipColors[row.status]?.border || "#cbd5e1",
                        },
                        ".MuiSelect-select": {
                          paddingTop: "2px",
                          paddingBottom: "2px",
                          paddingLeft: "10px",
                        },
                        ".MuiSvgIcon-root": {
                          color:
                            statusChipColors[row.status]?.color || "#475569",
                        },
                      }}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </td>
                  <td>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedApplicant(row)}
                      title="View Candidate Profile"
                      sx={{ color: "#475569" }}
                    >
                      <Eye size={17} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {pagination.totalPages > 1 && (
          <div className="module-list-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages} (
              {pagination.total} total)
            </span>
            <div className="module-list-pagination-btns">
              <button
                type="button"
                className="module-btn secondary"
                disabled={page <= 1}
                onClick={() =>
                  setQuery({ page: String(page - 1) }, { resetPage: false })
                }
              >
                Previous
              </button>
              <button
                type="button"
                className="module-btn secondary"
                disabled={page >= pagination.totalPages}
                onClick={() =>
                  setQuery({ page: String(page + 1) }, { resetPage: false })
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={Boolean(selectedApplicant)}
        onClose={() => setSelectedApplicant(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Applicant Profile
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          {selectedApplicant && (
            <>
              <div>
                <strong style={{ fontSize: "1.15rem", color: "#0f172a" }}>
                  {selectedApplicant.fullName}
                </strong>
                {selectedApplicant.gender && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: "0.82rem",
                      color: "#475569",
                      background: "#f1f5f9",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {selectedApplicant.gender}
                  </span>
                )}
                <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: 4 }}>
                  Applied: {formatAppliedDate(selectedApplicant.createdAt)}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Email
                  </div>
                  <div style={{ marginTop: 2 }}>
                    <Link
                      href={`mailto:${selectedApplicant.email}`}
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.9rem" }}
                    >
                      <Mail size={14} /> {selectedApplicant.email}
                    </Link>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Phone
                  </div>
                  <div style={{ marginTop: 2 }}>
                    {selectedApplicant.phone ? (
                      <Link
                        href={`tel:${selectedApplicant.phone}`}
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.9rem" }}
                      >
                        <Phone size={14} /> {selectedApplicant.phone}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Role Applied
                  </div>
                  <div style={{ fontWeight: 500, marginTop: 2 }}>
                    {selectedApplicant.job?.title || selectedApplicant.jobTitle || "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Total Experience
                  </div>
                  <div style={{ fontWeight: 500, marginTop: 2 }}>
                    {selectedApplicant.experience ? `${selectedApplicant.experience} Years` : "Fresher / None"}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Current CTC
                  </div>
                  <div style={{ fontWeight: 500, marginTop: 2 }}>
                    {selectedApplicant.currentCtc ? `₹${selectedApplicant.currentCtc} Lakhs / Year` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Expected CTC
                  </div>
                  <div style={{ fontWeight: 500, marginTop: 2 }}>
                    {selectedApplicant.expectedCtc ? `₹${selectedApplicant.expectedCtc} Lakhs / Year` : "—"}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Where Heard About Job
                  </div>
                  <div style={{ marginTop: 2 }}>{selectedApplicant.hearAbout || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Reference / Referred By
                  </div>
                  <div style={{ marginTop: 2 }}>{selectedApplicant.referredBy || "—"}</div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                  Social / Profile Link
                </div>
                <div style={{ marginTop: 2 }}>
                  {selectedApplicant.linkedin ? (
                    <Link
                      href={
                        selectedApplicant.linkedin.startsWith("http")
                          ? selectedApplicant.linkedin
                          : `https://${selectedApplicant.linkedin}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.9rem" }}
                    >
                      <ExternalLink size={14} /> {selectedApplicant.linkedin}
                    </Link>
                  ) : (
                    "—"
                  )}
                </div>
              </div>

              {selectedApplicant.resumeUrl && (
                <div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                    Resume / CV
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileText size={16} />}
                      onClick={() => handleViewResume(selectedApplicant)}
                      disabled={viewingResumeId === String(selectedApplicant.id)}
                    >
                      {viewingResumeId === String(selectedApplicant.id)
                        ? "Opening Resume…"
                        : "View / Download Resume"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedApplicant(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Applicants;
